import log from 'loglevel'
import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { MemoryRouter } from 'react-router'

export const printComponentAsPDF = (
  component: JSX.Element,
  title: string,
): void => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    log.error('printComponentAsPDF: Cannot run in server-side environment')
    return
  }

  try {
    const componentString = ReactDOMServer.renderToString(
      <MemoryRouter initialEntries={['/']}>{component}</MemoryRouter>,
    )
    const styles = Array.from(document.querySelectorAll('style'))
      .map((style) => style.outerHTML)
      .join('')

    let iframe = document.getElementById('printFrame') as HTMLIFrameElement

    if (!iframe) {
      iframe = document.createElement('iframe')
      iframe.id = 'printFrame'
      iframe.style.display = 'none'
      document.body.appendChild(iframe)
    }

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document

    if (iframeDoc) {
      iframeDoc.open()
      iframeDoc.write(`<html><head><title>${title}</title>`)
      iframeDoc.write(styles) // inject the styles
      iframeDoc.write('</head><body>')
      iframeDoc.write(componentString)
      iframeDoc.write('</body></html>')
      iframeDoc.close()

      const printFrameWindow = iframe.contentWindow
      printFrameWindow?.print()
    }
  } catch (error) {
    log.error('Error in printComponentAsPDF:', error)
    // Fallback: try to print the current page
    window.print()
  }
}

export const printElementAsPDF = async (
  element: HTMLDivElement,
  title: string,
): Promise<void> => {
  if (!element) return

  const content = element.innerHTML
  const contentOuterHTML = element.outerHTML
  const printWindow = window.open('', '_blank', 'width=1600,height=600')
  if (printWindow && content) {
    // Copy <style> and <link rel="stylesheet"> from the main doc
    const styles = Array.from(document.styleSheets)
      .map((styleSheet) => {
        try {
          return Array.from(styleSheet.cssRules)
            .map((rule) => rule.cssText)
            .join('\n')
        } catch (e) {
          // CORS-protected stylesheet
          if ((styleSheet as CSSStyleSheet).href) {
            return `<link rel="stylesheet" href="${
              (styleSheet as CSSStyleSheet).href
            }">`
          }
          return ''
        }
      })
      .join('\n')

    const scaledStyles = [
      styles,
      `
          body {
            zoom: 0.4;
          }
      `,
    ].join('\n')

    printWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            ${scaledStyles}
          </style>
        </head>
        <body>
          ${contentOuterHTML}
        </body>
      </html>
    `)

    printWindow.document.close()
    printWindow.focus()

    // Delay helps ensure styles load before print
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 1000)
  }
}

export const blobToUint8Array = async (blob: Blob): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const arrayBuffer = reader.result as ArrayBuffer
      const uint8Array = new Uint8Array(arrayBuffer)
      resolve(uint8Array)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}
